import { PaymentErrorCode } from "./payment-error-codes";
import type { ComposerTranslation } from "vue-i18n";

interface ICyberSourceErrorDetail {
  message: string;
  location: string;
}

interface ICyberSourceErrorWithDetails {
  details?: ICyberSourceErrorDetail[];
  reason?: string;
}

type CyberSourceErrorType = ICyberSourceErrorWithDetails | unknown;

interface IErrorCheckResult {
  isExpirationError: boolean;
  isInvalidPhoneNumberError: boolean;
  isBillingAddressEmailError: boolean;
  isInvalidBillingAddressError: boolean;
  isTooManyAttemptsError: boolean;
  isPaymentDataExpiredError: boolean;
  isAuthenticationValidationError: boolean;
  is478DeclinedError: boolean;
}

const TOO_MANY_ATTEMPTS_REASON = "CREATE_TOKEN_CAPTURE_CONTEXT_USED_TOO_MANY_TIMES";
const PAYMENT_DATA_EXPIRED_REASON = "CREATE_TOKEN_VALIDATION_SERVERSIDE";
const AUTHENTICATION_VALIDATION_FAILED_REASON = "AUTHENTICATION_VALIDATION_FAILED";
const PAYMENT_DECLINED_478_PATTERN = PaymentErrorCode.PaymentDeclined478;

/**
 * Checks if the error indicates an expiration date issue.
 * Supports backend error code and client-side Flex Microform details array.
 */
const hasExpirationDateError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  // Check for Flex Microform details array
  const errorWithDetails = error as ICyberSourceErrorWithDetails;
  if (!("details" in errorWithDetails) || !Array.isArray(errorWithDetails.details)) {
    return false;
  }

  return errorWithDetails.details.some(
    (detail) => detail.location === "expirationYear" || detail.location === "expirationMonth",
  );
};

/**
 * Checks if the error message contains a pattern indicating expiration error
 */
const hasExpirationErrorInMessage = (errorMessage?: string): boolean => {
  return !!errorMessage?.includes("expiration") || errorMessage === PaymentErrorCode.InvalidExpirationDate;
};

/**
 * Checks if the error message contains a pattern indicating invalid phone number
 */
const hasInvalidPhoneNumberInMessage = (errorMessage?: string): boolean => {
  return errorMessage === PaymentErrorCode.InvalidPhoneNumber;
};

/**
 * Checks if the error message contains a pattern indicating invalid billing email
 */
const hasBillingAddressEmailErrorInMessage = (errorMessage?: string): boolean => {
  return errorMessage === PaymentErrorCode.InvalidBillingEmail;
};

/**
 * Checks if the error message contains a pattern indicating invalid billing address field
 */
const hasInvalidBillingAddressInMessage = (errorMessage?: string): boolean => {
  return errorMessage === PaymentErrorCode.InvalidBillingAddress;
};

/**
 * Checks if the error indicates too many token creation attempts
 */
const hasTooManyAttemptsError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const errorWithReason = error as ICyberSourceErrorWithDetails;
  return "reason" in errorWithReason && errorWithReason.reason === TOO_MANY_ATTEMPTS_REASON;
};

/**
 * Checks if the error indicates payment data has expired
 */
const hasPaymentDataExpiredError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const errorWithReason = error as ICyberSourceErrorWithDetails;
  return (
    "reason" in errorWithReason &&
    errorWithReason.reason === PAYMENT_DATA_EXPIRED_REASON &&
    (!errorWithReason.details || errorWithReason.details.length === 0)
  );
};

/**
 * Checks if the error indicates authentication validation failed
 */
const hasAuthenticationValidationError = (error: unknown | string | undefined): boolean => {
  if (typeof error === "string") {
    return error.includes(AUTHENTICATION_VALIDATION_FAILED_REASON);
  }

  if (typeof error !== "object" || error === null) {
    return false;
  }

  const errorWithReason = error as ICyberSourceErrorWithDetails;
  return "reason" in errorWithReason && errorWithReason.reason === AUTHENTICATION_VALIDATION_FAILED_REASON;
};

/**
 * Checks if the error message contains a 478 decline pattern
 */
const has478DeclinedErrorInMessage = (errorMessage?: string): boolean => {
  return !!errorMessage?.includes(PAYMENT_DECLINED_478_PATTERN) || errorMessage === PaymentErrorCode.PaymentDeclined478;
};

/**
 * Checks if the error (object or string) indicates a 478 decline
 */
const has478DeclinedError = (error: unknown): boolean => {
  if (typeof error === "string") {
    return has478DeclinedErrorInMessage(error);
  }
  return false;
};

/**
 * Analyzes a CyberSource error (exception object or error message string) and returns which error types are present
 */
export const analyzeCyberSourceError = (error: CyberSourceErrorType | string | undefined): IErrorCheckResult => {
  if (typeof error === "string" || error === undefined) {
    return {
      isExpirationError: hasExpirationErrorInMessage(error),
      isInvalidPhoneNumberError: hasInvalidPhoneNumberInMessage(error),
      isBillingAddressEmailError: hasBillingAddressEmailErrorInMessage(error),
      isInvalidBillingAddressError: hasInvalidBillingAddressInMessage(error),
      isTooManyAttemptsError: false,
      isPaymentDataExpiredError: false,
      isAuthenticationValidationError: hasAuthenticationValidationError(error),
      is478DeclinedError: has478DeclinedErrorInMessage(error),
    };
  }

  return {
    isExpirationError: hasExpirationDateError(error),
    isInvalidPhoneNumberError: false,
    isBillingAddressEmailError: false,
    isInvalidBillingAddressError: false,
    isTooManyAttemptsError: hasTooManyAttemptsError(error),
    isPaymentDataExpiredError: hasPaymentDataExpiredError(error),
    isAuthenticationValidationError: hasAuthenticationValidationError(error),
    is478DeclinedError: has478DeclinedError(error),
  };
};

/**
 * Returns the appropriate translated error message based on the error check result
 */
/**
 * Determines whether the payment form should be reinitialized based on the error result.
 * Returns true if the error is related to too many attempts, expired payment data, expiration,
 * or if no specific error was identified.
 */
export const shouldReinitPaymentForm = (errorResult: IErrorCheckResult): boolean => {
  const hasSpecificError =
    errorResult.isExpirationError ||
    errorResult.isInvalidPhoneNumberError ||
    errorResult.isBillingAddressEmailError ||
    errorResult.isInvalidBillingAddressError ||
    errorResult.isTooManyAttemptsError ||
    errorResult.isPaymentDataExpiredError ||
    errorResult.isAuthenticationValidationError ||
    errorResult.is478DeclinedError;

  return (
    errorResult.isTooManyAttemptsError ||
    errorResult.isPaymentDataExpiredError ||
    errorResult.isExpirationError ||
    errorResult.isAuthenticationValidationError ||
    errorResult.is478DeclinedError ||
    !hasSpecificError
  );
};

/**
 * Returns the appropriate translated error message based on the error check result.
 * Uses `defaultErrorKey` as a fallback when no specific error type is detected.
 */
export const getCyberSourceErrorMessage = (
  errorResult: IErrorCheckResult,
  t: ComposerTranslation,
  defaultErrorKey: string = "shared.payment.bank_card_form.default_authorization_error_modal",
): string => {
  if (errorResult.is478DeclinedError) {
    return t("shared.payment.bank_card_form.payment_declined_478");
  }
  if (errorResult.isAuthenticationValidationError) {
    return t("shared.payment.bank_card_form.authentication_validation_failed");
  }
  if (errorResult.isTooManyAttemptsError) {
    return t("shared.payment.bank_card_form.too_many_attempts");
  }
  if (errorResult.isPaymentDataExpiredError) {
    return t("shared.payment.bank_card_form.payment_modal_expired");
  }
  if (errorResult.isExpirationError) {
    return t("shared.payment.bank_card_form.invalid_expiration_date");
  }
  if (errorResult.isInvalidPhoneNumberError) {
    return t("shared.payment.bank_card_form.invalid_phone_number");
  }
  if (errorResult.isBillingAddressEmailError) {
    return t("shared.payment.bank_card_form.invalid_billing_address_email");
  }
  if (errorResult.isInvalidBillingAddressError) {
    return t("shared.payment.bank_card_form.invalid_billing_address");
  }
  return t(defaultErrorKey);
};
